import { useState, type FormEvent } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/movies';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      setFormMessage('提交成功！感谢您的留言，我们会尽快回复。');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ng0r68mbnljin5a010', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('提交成功！感谢您的留言，我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormState('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 品牌介绍 | 6080影院"
        description="6080影院致力于为广大影迷提供高清电影免费在线观看服务。了解我们的品牌故事、用户口碑和联系方式，高清在线影院级画质，免费影视大全。"
        keywords="6080影院,关于我们,品牌介绍,联系方式,在线影院"
        canonicalUrl="https://www.6080cinema.com/about"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于6080影院',
            description: '6080影院致力于为广大影迷提供高清电影免费在线观看服务，高清在线影院级画质，免费影视大全无需付费。',
            url: 'https://www.6080cinema.com/about',
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="relative pt-20 pb-16 md:pb-20 bg-background-100">
            <div className="container-site">
              <div className="text-center max-w-2xl mx-auto">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  关于 <span className="text-primary-500">6080影院</span>
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  6080影院成立于2020年，是一个致力于为广大影迷提供高清电影免费在线观看的平台。
                  我们汇集了海量影视资源，涵盖动作片、喜剧片、爱情片、科幻片、恐怖片、动漫、电视剧、纪录片等多种类型，
                  以高清在线影院级画质和流畅的播放体验，为数百万用户带来了优质的观影享受。
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="container-site">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/10 flex items-center justify-center">
                    <i className="ri-movie-line text-primary-500 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-800 mb-2">海量资源</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    涵盖超过20,000部高清电影和电视剧，类型丰富，每日更新，满足您的各种观影需求。
                  </p>
                </div>

                <div className="p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-500/10 flex items-center justify-center">
                    <i className="ri-4k-line text-accent-500 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-800 mb-2">高清画质</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    采用先进的视频编码和CDN加速技术，提供高清在线影院级画质，播放流畅不卡顿。
                  </p>
                </div>

                <div className="p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-500/10 flex items-center justify-center">
                    <i className="ri-shield-check-line text-secondary-500 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-800 mb-2">完全免费</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    无需注册，无需付费，无需安装任何插件。打开网页即可畅享高清影视资源，真正实现免费影视大全。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100/50">
            <div className="container-site">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 mt-1">听听我们的用户怎么说</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="p-5 rounded-lg bg-background-100 border border-background-200"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-500 font-bold text-sm">
                          {t.name.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${
                                i < t.rating
                                  ? 'ri-star-fill text-accent-500'
                                  : 'ri-star-line text-foreground-300'
                              }`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed">{t.content}</p>
                    <p className="text-xs text-foreground-400 mt-3">{t.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="container-site max-w-2xl">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  有任何问题或建议？请随时与我们联系，我们会在24小时内回复您。
                </p>
              </div>

              <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    className="form-antispam-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {formMessage && (
                    <div
                      className={`p-3 rounded-md text-sm ${
                        formState === 'success'
                          ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                          : 'bg-red-500/10 border border-red-500/20 text-red-400'
                      }`}
                    >
                      {formMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {formState === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        提交中...
                      </>
                    ) : (
                      '提交留言'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}